import type { FC } from 'react';
import React from 'react';
import { css } from '@compiled/react';
import { useQuery } from '@apollo/react-hooks';

import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { isFedRamp } from '@atlassian/atl-context';

import { ErrorDisplay } from '@confluence/error-boundary';
import { useSessionData } from '@confluence/session-data';
import { QuickSummaryPopup } from '@confluence/quick-summary';

import type { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../../../Feed';

import { FeedCardActionsMenuQuery } from './FeedCardActionsMenuQuery.graphql';
import { FeedCardStarAction } from './FeedCardStarAction';
import { FeedCardWatchAction } from './FeedCardWatchAction';
import { FeedCardShareAction } from './FeedCardShareAction';
import type {
	FeedCardActionsMenuQuery as FeedCardActionsMenuQueryType,
	FeedCardActionsMenuQueryVariables,
} from './__types__/FeedCardActionsMenuQuery';
import { ActionButtonWrapper } from './ActionButtonStyles';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const actionsWrapperStyle = css({
	display: 'flex',
	alignItems: 'center',
});

const QuickSummaryButtonContainer = xcss({
	minWidth: '118px',
});

const QuickSummaryButtonStyles = {
	border: `1.5px solid ${token('color.border', '#DFE1E6')}`,
	borderRadius: '4px',
	padding: '3px 7px',
};

const ListItemWrapperStyles = xcss({
	listStyleType: 'none',
	margin: '0',
	padding: '0',
});

type Props = {
	contentId: string;
	contentUrl: string;
	contentTitle: string;
	spaceKey?: string;
	contentType: 'page' | 'blogpost';
	contentSubType?: string | null;
	setIsActionMenuOpen: (open: boolean) => void;
	hasCardBeenHovered: boolean;
	source: string;
	lastModifiedDate: string;
	popularFeedTimeGranularity?: PopularFeedTimeGranularityValue;
	followingFeedSortby?: FollowingFeedSortByValue;
};

export const FeedCardActions: FC<Props> = ({
	contentId,
	contentUrl,
	contentTitle,
	contentType,
	spaceKey,
	contentSubType,
	setIsActionMenuOpen,
	hasCardBeenHovered,
	source,
	lastModifiedDate,
	popularFeedTimeGranularity,
	followingFeedSortby,
}) => {
	const { data, error } = useQuery<FeedCardActionsMenuQueryType, FeedCardActionsMenuQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		FeedCardActionsMenuQuery,
		{
			variables: { contentId },
		},
	);

	const { isAdminHubAIEnabled } = useSessionData();

	const renderStarAction = () => {
		if (error) {
			return <ErrorDisplay error={error} />;
		}

		const isStarred = !!data?.singleContent?.metadata?.currentuser?.favourited?.isFavourite;
		return <FeedCardStarAction contentId={contentId} isStarred={isStarred} />;
	};

	return (
		<div css={actionsWrapperStyle} data-testid="feed-card-actions">
			<Flex as="ul" alignItems="center">
				<Box xcss={ListItemWrapperStyles} as="li">
					<FeedCardShareAction
						contentId={contentId}
						contentUrl={contentUrl}
						contentTitle={contentTitle}
						contentType={contentType}
						contentSubType={contentSubType}
						setIsMenuOpen={setIsActionMenuOpen}
						popularFeedTimeGranularity={popularFeedTimeGranularity}
						followingFeedSortby={followingFeedSortby}
					/>
				</Box>
				<Box xcss={ListItemWrapperStyles} as="li">
					<FeedCardWatchAction
						contentId={contentId}
						setIsMenuOpen={setIsActionMenuOpen}
						hasCardBeenHovered={hasCardBeenHovered}
						contentType={contentType}
						popularFeedTimeGranularity={popularFeedTimeGranularity}
						followingFeedSortby={followingFeedSortby}
					/>
				</Box>
				<Box xcss={ListItemWrapperStyles} as="li">
					{renderStarAction()}
				</Box>

				{isAdminHubAIEnabled && !isFedRamp() && (
					<Box xcss={ListItemWrapperStyles} as="li">
						<ActionButtonWrapper>
							<Box xcss={QuickSummaryButtonContainer}>
								<QuickSummaryPopup
									contentPath={contentUrl}
									contentId={contentId}
									spaceKey={spaceKey}
									contentType={contentType}
									source={source}
									lastModifiedDate={lastModifiedDate}
									setIsFeedMenuOpen={setIsActionMenuOpen}
									buttonStyles={QuickSummaryButtonStyles}
									popularFeedTimeGranularity={popularFeedTimeGranularity}
									followingFeedSortby={followingFeedSortby}
								/>
							</Box>
						</ActionButtonWrapper>
					</Box>
				)}
			</Flex>
		</div>
	);
};
